// EmployeeDetailPage.js
// Detail page for individual employee with payroll history

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PaymentsIcon from '@mui/icons-material/Payments';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getEmployee, getEmployeePayrollHistory } from '../services/employeeService';
import EmployeeEditDialog from './dialogs/EmployeeEditDialog';
import PayrollSubmissionDialog from './dialogs/PayrollSubmissionDialog';

const monthNames = [
  '', 'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
];

const getStatusColor = (status) => {
  switch (status) {
    case 'active': return 'success';
    case 'inactive': return 'warning';
    case 'terminated': return 'error';
    default: return 'default';
  }
};

const EmployeeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Dialog states
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPayrollDialog, setShowPayrollDialog] = useState(false);

  // Fetch employee data
  const { data: employee, isLoading: isLoadingEmployee, error: employeeError } = useQuery({
    queryKey: ['employee', id],
    queryFn: () => getEmployee(id),
    enabled: !!id,
    staleTime: 30 * 1000,
  });

  // Fetch payroll history
  const { data: payrollHistory, isLoading: isLoadingPayroll } = useQuery({
    queryKey: ['employeePayrollHistory', id],
    queryFn: () => getEmployeePayrollHistory(id),
    enabled: !!id,
    staleTime: 30 * 1000,
  });

  const handleBack = () => {
    navigate('/employees');
  };

  const handleEditClose = () => {
    setShowEditDialog(false);
    queryClient.invalidateQueries({ queryKey: ['employee', id] });
  };

  const handlePayrollClose = () => {
    setShowPayrollDialog(false);
    queryClient.invalidateQueries({ queryKey: ['employeePayrollHistory', id] });
  };

  const payrollItems = payrollHistory?.items || [];

  if (isLoadingEmployee) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (employeeError || !employee) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">שגיאה בטעינת פרטי העובד</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2, direction: 'ltr' }}>
          חזרה לרשימה
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">
            {employee.FirstName} {employee.LastName}
          </Typography>
          <Chip
            label={employee.StatusDisplay}
            color={getStatusColor(employee.Status)}
            size="small"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, direction: 'ltr' }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setShowEditDialog(true)}
            sx={{ direction: 'ltr' }}
          >
            עריכה
          </Button>
          {employee.Status === 'active' && (
            <Button
              variant="contained"
              startIcon={<PaymentsIcon />}
              onClick={() => setShowPayrollDialog(true)}
              sx={{ direction: 'ltr' }}
            >
              הגש משכורת
            </Button>
          )}
        </Box>
      </Box>

      {/* Employee Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>פרטי עובד</Typography>
          <Grid container spacing={3} direction="row-reverse">
            {/* Personal Info */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, rowGap: 1.5 }}>
                {employee.IDNumber && (
                  <>
                    <Typography variant="body1" color="text.secondary">ת.ז.:</Typography>
                    <Typography variant="body1">{employee.IDNumber}</Typography>
                  </>
                )}
                {employee.EmployeeNumber && (
                  <>
                    <Typography variant="body1" color="text.secondary">מספר עובד:</Typography>
                    <Typography variant="body1">{employee.EmployeeNumber}</Typography>
                  </>
                )}
                {employee.PhoneNumber && (
                  <>
                    <Typography variant="body1" color="text.secondary">טלפון:</Typography>
                    <Typography variant="body1">{employee.PhoneNumber}</Typography>
                  </>
                )}
                {employee.Email && (
                  <>
                    <Typography variant="body1" color="text.secondary">אימייל:</Typography>
                    <Typography variant="body1">{employee.Email}</Typography>
                  </>
                )}
                {employee.Address && (
                  <>
                    <Typography variant="body1" color="text.secondary">כתובת:</Typography>
                    <Typography variant="body1">{employee.Address}</Typography>
                  </>
                )}
              </Box>
            </Grid>

            {/* Employment Info */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, rowGap: 1.5 }}>
                <Typography variant="body1" color="text.secondary">מחלקות:</Typography>
                <Typography variant="body1">
                  {employee.DepartmentNames?.length > 0
                    ? employee.DepartmentNames.join(', ')
                    : '-'}
                </Typography>

                <Typography variant="body1" color="text.secondary">סוג עובד:</Typography>
                <Typography variant="body1">{employee.EmployeeTypeName || '-'}</Typography>

                <Typography variant="body1" color="text.secondary">תאריך התחלה:</Typography>
                <Typography variant="body1">
                  {employee.HireDate
                    ? new Date(employee.HireDate).toLocaleDateString('he-IL')
                    : '-'}
                </Typography>

                {employee.TerminationDate && (
                  <>
                    <Typography variant="body1" color="text.secondary">תאריך סיום:</Typography>
                    <Typography variant="body1">
                      {new Date(employee.TerminationDate).toLocaleDateString('he-IL')}
                    </Typography>
                  </>
                )}

                <Divider sx={{ gridColumn: '1 / -1', my: 1 }} />

                <Typography variant="body1" color="text.secondary">סוג שכר:</Typography>
                <Typography variant="body1">{employee.WageTypeDisplay}</Typography>

                <Typography variant="body1" color="text.secondary">שכר:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  ₪{parseFloat(employee.WageAmount).toLocaleString()}
                  {employee.WageType === 'hourly' ? ' לשעה' : employee.WageType === 'daily' ? ' ליום' : ' לחודש'}
                </Typography>
              </Box>
            </Grid>

            {/* Notes */}
            {employee.Notes && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body1" color="text.secondary">הערות:</Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>{employee.Notes}</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Payroll History */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>היסטוריית משכורות</Typography>

        {isLoadingPayroll ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : payrollItems.length === 0 ? (
          <Alert severity="info">אין היסטוריית משכורות</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>תקופה</TableCell>
                  <TableCell>סוג שכר</TableCell>
                  <TableCell>שעות</TableCell>
                  <TableCell>סכום בסיס</TableCell>
                  <TableCell>ניכויים</TableCell>
                  <TableCell>תוספות</TableCell>
                  <TableCell>סה"כ</TableCell>
                  <TableCell>תאריך הגשה</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payrollItems.map((item) => (
                  <TableRow key={item.SubmissionID} hover>
                    <TableCell>
                      {monthNames[item.PeriodMonth]} {item.PeriodYear}
                    </TableCell>
                    <TableCell>{item.WageTypeDisplay}</TableCell>
                    <TableCell>
                      {(item.WageType === 'hourly' || item.WageType === 'daily') ? item.HoursWorked : '-'}
                    </TableCell>
                    <TableCell>₪{parseFloat(item.BaseAmount || 0).toLocaleString()}</TableCell>
                    <TableCell sx={{ color: item.Deductions > 0 ? 'error.main' : 'inherit' }}>
                      {item.Deductions > 0 ? `-₪${parseFloat(item.Deductions).toLocaleString()}` : '-'}
                    </TableCell>
                    <TableCell sx={{ color: item.Additions > 0 ? 'success.main' : 'inherit' }}>
                      {item.Additions > 0 ? `+₪${parseFloat(item.Additions).toLocaleString()}` : '-'}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">
                        ₪{parseFloat(item.TotalAmount || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.SubmittedAt
                        ? new Date(item.SubmittedAt).toLocaleDateString('he-IL')
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialogs */}
      <EmployeeEditDialog
        open={showEditDialog}
        onClose={handleEditClose}
        employee={employee}
      />

      <PayrollSubmissionDialog
        open={showPayrollDialog}
        onClose={handlePayrollClose}
        employee={employee}
      />
    </Box>
  );
};

export default EmployeeDetailPage;
